import { useState } from 'react';
import { debunkedItems, confirmedItems, pendingItems } from '@/mocks/siteContent';
import { SectionHead } from './kit';

type StateKey = 'debunked' | 'confirmed' | 'pending';

const stateMap: Record<StateKey, { items: typeof confirmedItems; tint: string }> = {
  debunked: { items: debunkedItems, tint: 'bg-secondary-100 text-secondary-900' },
  confirmed: { items: confirmedItems, tint: 'bg-primary-100 text-primary-800' },
  pending: { items: pendingItems, tint: 'bg-accent-100 text-accent-900' },
};

export default function Ledger() {
  const [active, setActive] = useState<StateKey>('debunked');
  const current = stateMap[active];
  const tabs: { key: StateKey; label: string; count: number }[] = [
    { key: 'debunked', label: '已辟谣', count: debunkedItems.length },
    { key: 'confirmed', label: '已实锤', count: confirmedItems.length },
    { key: 'pending', label: '求证中', count: pendingItems.length },
  ];

  return (
    <section id="ledger" className="border-y border-background-200 bg-background-100/70">
      <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <SectionHead
          index="壹"
          kicker="辟谣与实锤"
          title="求证台账：每条结论都附依据"
          desc="把五一期间流传的网传消息逐条编号归档，按「已辟谣 / 已实锤 / 求证中」分流呈现。定论仅依托公开出处与官方口径，不采信无原始出处的二手截图。"
        />

        {/* segmented control */}
        <div className="mb-8 inline-flex rounded-full border border-background-200 bg-background-50 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              aria-pressed={active === t.key}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors md:px-5 ${
                active === t.key
                  ? 'bg-foreground-950 text-background-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t.label}
              <span
                className={`ml-1.5 text-xs ${
                  active === t.key ? 'text-background-200' : 'text-foreground-500'
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>

        <ol className="space-y-3">
          {current.items.map((item, i) => (
            <li
              key={item.title}
              className="grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-50 p-5 md:grid-cols-12 md:items-start md:gap-6"
            >
              <div className="md:col-span-1">
                <span className="font-heading text-lg font-bold text-background-300">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <div className="md:col-span-8">
                <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.detail}</p>
              </div>
              <div className="flex md:col-span-3 md:justify-end">
                <span className={`inline-flex h-fit items-center rounded-full px-3 py-1.5 text-sm font-semibold ${stateMap[active].tint}`}>
                  {item.verdict}
                </span>
              </div>
            </li>
          ))}
        </ol>

        <p className="mt-6 text-xs text-foreground-500">
          * 台账按日期归档，结论更新时会保留原始留档；如需逐条原文出处可参照各条下方的来源编号。
        </p>
      </div>
    </section>
  );
}